// src/App.js
import React from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes} from 'react-router-dom';
import {AuthProvider, useAuth} from './contexts/AuthContext';

// Pages publiques
import HomePage from './pages/HomePage';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';

// Pages joueur
import PlayerDashboard from './pages/player/PlayerDashboard';
import TrainingPage from './pages/player/TrainingPage';
import QuizPage from './pages/player/QuizPage';
import QuizResults from './pages/player/QuizResults';
import ProfilePage from './pages/ProfilePage';

// Pages admin
import AdminDashboard from './pages/admin/AdminDashboard';
import QuizForm from './pages/admin/QuizForm';
import ErrorBoundary from "./components/ErrorBoundary";
import AdminQuestions from "./pages/admin/AdminQuestion";
import AdminPlayers from "./pages/admin/AdminPlayers";
import AdminAnnouncements from "./pages/admin/AdminAnnouncement";
import PlayerDetail from "./pages/player/PlayerDetail";
import ForgotPassword from "./components/auth/ForgotPassword";

// Routes protégées
const PrivateRoute = ({children, requiredRole}) => {
    const {currentUser, userRole} = useAuth();

    if (!currentUser) {
        return <Navigate to="/login"/>;
    }

    if (requiredRole && userRole !== requiredRole) {
        return <Navigate to="/dashboard"/>;
    }

    return children;
};

function App() {
    return (
        <ErrorBoundary>
            <Router>
                <AuthProvider>
                    <Routes>
                        {/* Routes publiques */}
                        <Route path="/" element={<HomePage/>}/>
                        <Route path="/login" element={<Login/>}/>
                        <Route path="/forgot-password" element={<ForgotPassword/>}/>
                        <Route path="/signup" element={<Signup/>}/>

                        {/* Page de profil */}
                        <Route
                            path="/profile"
                            element={
                                <PrivateRoute>
                                    <ProfilePage/>
                                </PrivateRoute>
                            }
                        />

                        {/* Routes joueur */}
                        <Route
                            path="/dashboard"
                            element={
                                <PrivateRoute>
                                    <PlayerDashboard/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/training"
                            element={
                                <PrivateRoute>
                                    <TrainingPage/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/training/session"
                            element={
                                <PrivateRoute>
                                    <QuizPage/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/training/results/:sessionId"
                            element={
                                <PrivateRoute>
                                    <QuizResults/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/quiz/:quizId"
                            element={
                                <PrivateRoute>
                                    <QuizPage/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/quiz/:quizId/results/:sessionId"
                            element={
                                <PrivateRoute>
                                    <QuizResults/>
                                </PrivateRoute>
                            }
                        />

                        {/* Routes admin */}
                        <Route
                            path="/admin/dashboard"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <AdminDashboard/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/admin/quiz/new"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <QuizForm/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/admin/quiz/:quizId"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <QuizForm/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/admin/quiz/:quizId/results"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <QuizResults/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/admin/questions"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <AdminQuestions/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/admin/players"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <AdminPlayers/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/admin/announcements"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <AdminAnnouncements/>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/admin/players/:playerId"
                            element={
                                <PrivateRoute requiredRole="admin">
                                    <PlayerDetail/>
                                </PrivateRoute>
                            }
                        />

                        {/* Route par défaut - redirection vers l'accueil */}
                        <Route path="*" element={<Navigate to="/"/>}/>
                    </Routes>
                </AuthProvider>
            </Router>
        </ErrorBoundary>
    );
}

export default App;
